<template>
  <ShipmentMap
    v-if="showMap"
    :id="mapId"
    :key="`map-${order?.id}`"
    :shipment="selectedShipment"
    :static-map="staticMapUrl.data"
    :dynamic="showDynamicMap"
    :interactive="isMapActionsEnabled"
    :loading="staticMapUrl.loading || transportSegments.loading"
    :route-geo-json="routeGeoJson.data"
  />
  <Content
    :audit-log-modal-label="t('orders.orderNumParam', { param: orderId })"
    :audit-records="auditRecords"
    :action-records="actionRecords"
    :created-audit-record="createdAuditRecord"
    :header-dark-bg="showMap"
    :subheader="
      isOwningClientSubOfCurrentDomain
        ? t('orders.detailsForOrderId', { orderId })
        : undefined
    "
    :loading="loading || saving"
    :back-btn-route="{ name: 'orderList', query: { search: searchParams } }"
    :back-btn-label="t('orders.backToAllOrders')"
    :show-toolbar="isAnyDirty"
    class="order-detail"
    remove-card-padding
    remove-aside-card-padding
    @show-audit-log="loadActionAndAuditRecords"
  >
    <template #headerLeft>
      <ClientAvatarAndName
        v-if="isOwningClientSubOfCurrentDomain"
        :accent-color="clientBranding.data?.accentColor"
        :name="order.owner?.name"
        :logo="clientBranding.data?.avatar?.signedUrl"
        size="large"
      >
        <template #name="{ value }">
          <h1 id="content-heading" tabindex="-1">{{ value }}</h1>
        </template>
      </ClientAvatarAndName>
      <template v-else>
        <HeaderIcon icon="file-alt" />
        <h1 id="content-heading" tabindex="-1">
          {{ t('orders.detailsForOrderId', { orderId }) }}
        </h1>
      </template>

      <SwcChip v-if="order?.swc" />
      <CustomerRoutedChip v-else-if="order?.customerRouted" />
      <OrderChip v-else-if="order?.status" :status="order.status" />
      <TagContainer
        v-model="internalTags"
        :loading="tagOptions.loading"
        :options="tagOptions.data"
        :readonly="!$hasAuthority('order', 'update')"
        :route-builder="buildTagSearchRoute"
        :allow-deletes="canDeleteTags"
        @add="onAddTag"
        @remove="onRemoveTag"
        @delete="
          requestDeleteConfirmation({
            message: t('tags.tagWarningParam', {
              param: t('common.ordersAndShipments'),
            }),
            accept: () => onDelete($event),
          })
        "
      />
      <AssignedUserContainer
        id="assigned-user-container"
        v-model="order.assignedUsers"
        :options="assignableUsers"
        :disabled="!$hasAuthority('order', 'update')"
        @update:model-value="saveAssignedUsers"
      />
    </template>

    <template #headerRight>
      <div class="flex flex-gap-1 align-items-center">
        <ExpandButton
          v-if="showMap"
          :model-value="isMapExpanded"
          :loading="routeGeoJson.loading || staticMapUrl.loading"
          class="map-details-button p-button-text text-white flex"
          :target-id="mapId"
          :true-label="t('maps.hideDetails')"
          :false-label="t('maps.showDetails')"
          @update:model-value="onMapDetailsButtonClick"
        />
        <OrderActionButton
          :order="order"
          :shipments="relatedShipments"
          :ship-orders="shipOrders.data"
          data-testid="order-detail-action-button"
          @command="onOrderActionCommand"
        />
      </div>
    </template>

    <template #toolbar>
      <EditBar
        :loading="loading"
        :saving="saving"
        @save="saveOrder"
        @cancel="loadOrder(orderId)"
      />
    </template>

    <template #aside>
      <ReferenceNumbersSection
        id="reference-numbers"
        v-model="v$.referenceNumbers.$model"
        :resource-id="orderId"
        :level="ReferenceNumberTypeLevel.ORDER"
        :editable="
          $hasAuthority('referenceData', 'read') &&
          $hasAuthority('order', 'update') &&
          !orderEditingLocked
        "
        @change="numDirtyRefNums = $event.numDirty"
      />

      <GLCodeSection
        id="gl-codes"
        v-model="order.glCodes"
        :editable="
          $hasAuthority('glCode', 'read') &&
          $hasAuthority('order', 'update') &&
          !orderEditingLocked
        "
        :auto-gl-code-suggestion="order.autoGlCodeOverride?.suggestion"
        @change="numDirtyGlCodes = $event.numDirty"
      />

      <AccessorialSection
        id="accessorials"
        ref="accessorialSection"
        v-model:top-level-accessorials="order.requestedAccessorials"
        v-model:stop-level-accessorials="stopLevelAccessorials"
        :editable="
          $hasAuthority('order', 'update') &&
          $hasAuthority('referenceData', 'read') &&
          isStatusEqualOrBefore(order.status, OrderStatus.PENDING)
        "
        @change="numDirtyAccessorials = $event.numDirty"
      />

      <BillToSection
        id="bill-to"
        v-model:bill-to="v$.billTo.$model"
        :editable="
          $hasAuthority('order', 'update') &&
          isStatusEqualOrBefore(order?.status, OrderStatus.PENDING)
        "
      />

      <NoteSection
        id="notes"
        v-model:internal-notes="v$.internalNotes.$model"
        v-model:carrier-notes="v$.carrierNotes.$model"
        :editable="
          $hasAuthority('order', 'update') &&
          order.status !== OrderStatus.CANCELED
        "
      />

      <DocumentsSection
        v-if="$hasAuthority('document', 'read')"
        id="documents"
        :editable="
          $hasAuthority('order', 'update') &&
          $hasAuthority('document', 'create') &&
          !orderEditingLocked
        "
      />

      <RelatedDocumentSection
        v-if="$hasAuthority('document', 'read')"
        id="related-documents"
      />

      <Section
        v-if="order.status === OrderStatus.PENDING"
        id="basic-info"
        :header="t('common.basicInfo')"
        data-class="flex flex-gap-1 information-section"
      >
        <WrappedSwitch
          id="ship-without-contact"
          v-model="order.swc"
          :label="t('common.isSwcParam', { param: t('orders.orders') })"
          :disabled="order.customerRouted"
          @change="v$.swc.$anyDirty"
        >
          <template #label="{ inputId }">
            <i18n-t keypath="common.isSwcParam" tag="label" :for="inputId">
              <img
                src="@rygen/rygen-ui/assets/img/swc.svg"
                alt=""
                class="swc-icon"
                width="25"
                height="14"
              />

              <template #param>{{ t('orders.orders') }}</template>
            </i18n-t>
          </template>
        </WrappedSwitch>

        <WrappedSwitch
          id="customer-routed"
          v-model="order.customerRouted"
          :label="
            t('common.isCustomerRoutedParam', { param: t('orders.orders') })
          "
          :disabled="order.swc"
          @change="v$.customerRouted.$anyDirty"
        >
          <template #label="{ inputId }">
            <i18n-t
              keypath="common.isCustomerRoutedParam"
              tag="label"
              :for="inputId"
            >
              <img
                src="@/assets/img/customerRouted.svg"
                alt=""
                class="customer-routed-icon"
                width="20"
                height="10"
              />

              <template #param>{{ t('orders.orders') }}</template>
            </i18n-t>
          </template>
        </WrappedSwitch>
      </Section>

      <ValueOfGoodsSection
        id="value-of-goods"
        v-model:value-of-goods="v$.valueOfGoods.$model"
        v-model:line-items="v$.lineItems.$model"
        :editable="
          $hasAuthority('order', 'update') &&
          isStatusEqualOrBefore(order?.status, OrderStatus.PENDING)
        "
      />
    </template>

    <template
      v-if="
        isFinancialsKillSwitchEnabled &&
        $hasAuthority('shipment', 'readCostAllocation') &&
        hasShipmentCarrierRate &&
        !transportSegments.loading
      "
      #secondaryAside
    >
      <CostAllocationOrderCostSection
        id="cost"
        :order-id="orderId"
        :shipments="relatedShipments"
      />
    </template>

    <BlockUI :blocked="saving" full-screen />
    <section
      id="highlights"
      :aria-label="t('common.highlights', 2)"
      class="detail-section order-detail-header flex p-0 m-0"
      tabindex="-1"
    >
      <div class="order-detail-header-cell">
        <KeyValuePair
          :label="t('common.paramId', { param: t('orders.orders') })"
          :value="order.id"
          juicy
        />
      </div>

      <div class="order-detail-header-cell text-center">
        <KeyValuePair
          v-if="!transportSegments.data"
          :label="t('carriers.carriers')"
          juicy
        >
          <template #value>
            <Skeleton />
          </template>
        </KeyValuePair>
        <KeyValuePair
          v-else
          :label="t('carriers.carriers')"
          :value="selectedCarrierName"
          juicy
        />
      </div>

      <div class="order-detail-header-cell">
        <PopupDropdown
          id="direction"
          v-model="v$.direction.$model"
          :label="t('shipments.directions')"
          :options="availableDirections"
          :disabled="!isStatusEqualOrBefore(order?.status, OrderStatus.PENDING)"
          option-label="label"
          option-value="value"
          placeholder="-"
        >
          <template #placeholder="{ label, placeholder }">
            <KeyValuePair :value="placeholder" :label="label" juicy />
          </template>
        </PopupDropdown>
      </div>

      <div class="order-detail-header-cell">
        <PopupDropdown
          id="billing-terms"
          v-model="v$.billingTerms.$model"
          :label="t('shipments.billingTerms')"
          :options="availableBillingTerms"
          :disabled="!isStatusEqualOrBefore(order?.status, OrderStatus.PENDING)"
          option-label="label"
          option-value="value"
          placeholder="-"
        >
          <template #placeholder="{ label, placeholder }">
            <KeyValuePair :value="placeholder" :label="label" juicy />
          </template>
        </PopupDropdown>
      </div>

      <div class="order-detail-header-cell">
        <PopupDropdown
          id="requested-mode"
          v-model="v$.requestedMode.$model"
          :label="t('common.requestedMode')"
          :options="availableModes"
          :disabled="!isStatusEqualOrBefore(order?.status, OrderStatus.PENDING)"
          option-label="label"
          option-value="value"
          placeholder="-"
          clearable
        >
          <template #placeholder="{ label, placeholder }">
            <KeyValuePair :value="placeholder" :label="label" juicy />
          </template>
        </PopupDropdown>
      </div>

      <div class="order-detail-header-cell">
        <PopupDropdown
          id="equipment-type"
          v-model="v$.equipmentType.$model"
          :label="
            t('common.paramTypes', { param: t('referenceData.equipment') })
          "
          :options="availableEquipmentTypes"
          :disabled="!isStatusEqualOrBefore(order?.status, OrderStatus.PENDING)"
          :loading="clientEquipmentTypes.loading || selectedCarrier.loading"
          option-label="name"
          placeholder="-"
          clearable
        >
          <template #placeholder="{ label, placeholder }">
            <KeyValuePair :value="placeholder" :label="label" juicy />
          </template>
        </PopupDropdown>
      </div>
      <div class="order-detail-header-cell">
        <PopupSwitch
          v-model="v$.hot.$model"
          :label="t('common.hot')"
          :disabled="!isStatusEqualOrBefore(order?.status, OrderStatus.PENDING)"
        >
          <template #placeholder="{ label, placeholder }">
            <KeyValuePair
              :value="placeholder ? t('common.yes') : t('common.no')"
              :label="label"
              juicy
            />
          </template>
        </PopupSwitch>
      </div>
    </section>

    <section v-if="order.swc" class="detail-section">
      <SwcMessage :text="t('swc.swcOrderInfo')" />
    </section>
    <section v-else-if="order.customerRouted" class="detail-section">
      <CustomerRoutedMessage
        :text="t('customerRouted.customerRoutedOrderInfo')"
      />
    </section>

    <LocationSection
      id="locations"
      ref="locationSection"
      v-model="orderShippingPoints"
      :line-items="order.lineItems"
      :editable="
        $hasAuthority('order', 'update') &&
        isStatusEqualOrBefore(order?.status, OrderStatus.PENDING)
      "
      @change="numDirtyStops = $event.numDirty"
    />

    <OrderRateShop
      v-if="$hasAuthority('rateShop', 'read') && hasShipmentSpecs"
      id="rate-shop"
      ref="orderRateShop"
      v-model:freight-allowance="order.freightAllowance"
      :order="order"
      :can-view-uplift="canViewUplift"
      @rate-shop-completed="onRateShopCompleted"
      @request-spot-market-bid="confirmShowInviteCarriersToSpotMarket"
    />

    <OrderItinerarySection
      v-if="$hasAuthority('shipment', 'read') && isAssignedToShipment"
      id="itinerary"
      :ship-orders="shipOrders"
      :shipments="relatedShipments"
    />

    <LineItemSection
      id="line-items"
      ref="lineItemSection"
      v-model="v$.lineItems.$model"
      :editable="
        $hasAuthority('order', 'update') &&
        isStatusEqualOrBefore(order?.status, OrderStatus.PENDING)
      "
      @change="numDirtyLineItems = $event.numDirty"
    />

    <Section
      id="comments"
      :header="t('comments.comments', 2)"
      :loading="commentsLoading"
    >
      <CommentList
        :comments-with-replies="commentsWithReplies.data"
        :loading="commentsWithReplies.loading"
        :loading-more="commentsLoadingMore"
        :load-more-comments="loadMoreComments"
        :current-user="currentUser"
        :user-store="userStore"
        :comment-loading="commentLoading"
        :mentionable-users="mentionableUsers.data"
        :readonly="!hasUpdatePermission"
        @create="onCommentCreate"
        @update="onCommentUpdate"
        @reply="onCommentReply"
        @delete="
          requestDeleteConfirmation({
            param: t('comments.comments'),
            accept: () => onCommentDelete($event),
          })
        "
        @load-more="onCommentLoadMore"
        @load-more-reply="onCommentReplyLoadMore"
      />
    </Section>
  </Content>

  <OrderCancellationModal
    v-model:visible="isCancellationModalVisible"
    :has-shipments="isAssignedToShipment"
    @cancel-order="onCancelOrder"
  />

  <InviteCarriersToSpotMarketModal
    v-model:visible="inviteCarriersToSpotMarketModal.visible"
    :invite-command="inviteCarriersToSpotMarketModal.inviteCommand"
    :carrier-notes="order.carrierNotes"
    :can-view-uplift="canViewUplift"
    show-carrier-notes
    @cancel="onCancelInvite"
    @submit="onInviteCarriersToSpotMarket"
  />

  <SpotMarketErrorModal
    v-model:visible="spotMarketErrorModal.visible"
    :spot-markets="spotMarketErrorModal.erroredSpotMarkets"
  />

  <TrackingLinkModal
    v-model:visible="sharingModal.isVisible"
    :orders="shipmentTrackingUrl"
    @share-via-email="sendSharingEmails"
  />

  <OrderCloneModal
    v-model:visible="orderCloneModal.visible"
    v-model:clone-reference-numbers="orderCloneModal.cloneReferenceNumbers"
    v-model:clone-tags="orderCloneModal.cloneTags"
    v-model:skip-auto-actions="orderCloneModal.skipAutoActions"
    :show-clone-tags-option="orderCloneModal.showTagsOption"
    :show-clone-reference-numbers-option="
      orderCloneModal.showRefernceNumberOption
    "
    @confirm="onCloneOrder"
  />
</template>

<script lang="ts" setup>
import { CancelTokenSource, default as axios } from 'axios';
import debounce from 'lodash/debounce';
import uniqBy from 'lodash/uniqBy';
import { useConfirm } from 'primevue/useconfirm';
import type { Ref } from 'vue';
import {
  computed,
  nextTick,
  onBeforeUnmount,
  provide,
  ref,
  toRefs,
  useTemplateRef,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import authorization from '@rygen/rygen-ui/authorization';
import {
  required,
  useToast,
  useUid,
  useUnsavedChangesConfirmation,
  useValidations,
} from '@rygen/rygen-ui/composables';
import { useDeleteConfirmation } from '@rygen/rygen-ui/composables/useDeleteConfirmation';
import {
  lowerCase,
  mapEnumToTranslatedDropdownValues,
} from '@rygen/rygen-ui/i18n';
import {
  AccessorialCode,
  ActionCommand,
  BillingTerms,
  Direction,
  ErrorResponse,
  Loadable,
  Mode,
  ReferenceNumberTypeLevel,
  SharingEmailType,
  Tag,
  UserRef,
} from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import api from '@/api';
import {
  useActionLog,
  useKillSwitchFlag,
  useMap,
  useOrderRateShop,
  useRecentlyViewed,
  useOrderComments,
  useSpotMarketPolling,
} from '@/composables';
import {
  CancelOrderPayload,
  Carrier,
  CloneOrderCommand,
  DefaultOrderData,
  Order,
  OrderAction,
  OrderShippingPoint,
  OrderStatus,
  SharingRequest,
  Shipment,
  ShipmentSpecInviteCarriersToSpotMarketCommand,
  ShipmentTransportSegment,
  ShipOrder,
  SpotMarket,
} from '@/model';
import { useAssignableUserStore } from '@/stores/AssignableUser';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { useEquipmentTypeStore } from '@/stores/EquipmentType';
import { useParentEntityStore } from '@/stores/ParentEntity';
import { useSpotMarketStore } from '@/stores/SpotMarket';
import { useTagStore } from '@/stores/Tag';
import { useUserStore } from '@/stores/User.ts';
import {
  buildSearchRoute,
  getFlattenedFieldErrors,
  isBadRequest,
  isStatusEqualOrAfter,
  isStatusEqualOrBefore,
} from '@/util';

import AccessorialSection from '@/components/accessorial/AccessorialSection.vue';
import InviteCarriersToSpotMarketModal from '@/components/carrierPortal/InviteCarriersToSpotMarketModal.vue';
import SpotMarketErrorModal from '@/components/carrierPortal/SpotMarketErrorModal.vue';
import ClientAvatarAndName from '@/components/client/ClientAvatarAndName.vue';
import DocumentsSection from '@/components/document/DocumentsSection.vue';
import RelatedDocumentSection from '@/components/document/RelatedDocumentSection.vue';
import GLCodeSection from '@/components/glCode/GLCodeSection.vue';
import LineItemSection from '@/components/lineItem/LineItemSection.vue';
import BillToSection from '@/components/location/BillToSection.vue';
import LocationSection from '@/components/location/LocationSection.vue';
import NoteSection from '@/components/note/NoteSection.vue';
import OrderItinerarySection from '@/components/order/itinerary/OrderItinerarySection.vue';
import OrderActionButton from '@/components/order/OrderActionButton.vue';
import OrderCancellationModal from '@/components/order/OrderCancellationModal.vue';
import OrderChip from '@/components/order/OrderChip.vue';
import OrderCloneModal from '@/components/order/OrderCloneModal.vue';
import OrderRateShop from '@/components/order/rateShop/OrderRateShop.vue';
import ReferenceNumbersSection from '@/components/referenceNumber/ReferenceNumbersSection.vue';
import ShipmentMap from '@/components/shipment/ShipmentMap.vue';
import TrackingLinkModal from '@/components/shipment/tracking/TrackingLinkModal.vue';
import CostAllocationOrderCostSection from '@/components/shipmentFinancials/costAllocation/CostAllocationOrderCostSection.vue';
import CustomerRoutedChip from '@/components/ui/CustomerRoutedChip.vue';
import CustomerRoutedMessage from '@/components/ui/CustomerRoutedMessage.vue';
import EditBar from '@/components/ui/EditBar.vue';
import ValueOfGoodsSection from '@/components/valueOfGoods/ValueOfGoodsSection.vue';
import ExpandButton from '@rygen/rygen-ui/components/button/ExpandButton.vue';
import CommentList from '@rygen/rygen-ui/components/comment/CommentList.vue';
import Content from '@rygen/rygen-ui/components/content/Content.vue';
import HeaderIcon from '@rygen/rygen-ui/components/content/HeaderIcon.vue';
import AssignedUserContainer from '@rygen/rygen-ui/components/form/AssignedUserContainer.vue';
import WrappedSwitch from '@rygen/rygen-ui/components/form/WrappedSwitch.vue';
import PopupDropdown from '@rygen/rygen-ui/components/popup/PopupDropdown.vue';
import PopupSwitch from '@rygen/rygen-ui/components/popup/PopupSwitch.vue';
import SwcChip from '@rygen/rygen-ui/components/swc/SwcChip.vue';
import SwcMessage from '@rygen/rygen-ui/components/swc/SwcMessage.vue';
import TagContainer from '@rygen/rygen-ui/components/tag/TagContainer.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import BlockUI from 'primevue/blockui';
import Skeleton from 'primevue/skeleton';

const availableDirections = mapEnumToTranslatedDropdownValues(
  Direction,
  'Direction',
);

const availableBillingTerms = mapEnumToTranslatedDropdownValues(
  BillingTerms,
  'BillingTerms',
);

const availableModes = mapEnumToTranslatedDropdownValues(Mode, 'Mode');

const props = defineProps({
  orderId: {
    type: Number,
    required: true,
  },
});
const { orderId } = toRefs(props);

const order: Ref<Order> = ref(new Order());

const isFinancialsKillSwitchEnabled = useKillSwitchFlag(
  'tms-shipment-financials-kill-switch',
);

const accessorialSection = useTemplateRef('accessorialSection');
const lineItemSection = useTemplateRef('lineItemSection');
const locationSection = useTemplateRef('locationSection');
const orderRateShop = useTemplateRef('orderRateShop');

const {
  actionRecords,
  auditRecords,
  createdAuditRecord,
  loadActionAndAuditRecords,
} = useActionLog(order);
const { loadAssignableUsersByDomainId } = useAssignableUserStore();
const { handleRequiredDomainRedirect, currentUser } = useCurrentUserStore();
const { requestDeleteConfirmation } = useDeleteConfirmation();
const { equipmentTypes: clientEquipmentTypes, loadEquipmentTypes } =
  useEquipmentTypeStore();
const { t } = useI18n();
const { hasShipmentSpecs, loadRelatedShipmentSpecsAndRates, shipmentSpecs } =
  useOrderRateShop(order);
const {
  clearParentEntity,
  clientBranding,
  isOwningClientSubOfCurrentDomain,
  loadClientBranding,
  owningClient,
  setParentEntity,
} = useParentEntityStore();
useRecentlyViewed(order);
const router = useRouter();
const { inviteCarriersToSpotMarket } = useSpotMarketStore();
const { startSpotMarketPollingForResult } = useSpotMarketPolling();
const { loadTags, tags, deleteTag, tagOptions } = useTagStore();
const toast = useToast();
const { uid } = useUid('order-detail');
const userStore = useUserStore();
const {
  commentsWithReplies,
  commentsLoading,
  loadMoreComments,
  commentLoading,
  commentsLoadingMore,
  loadComments,
  onCommentCreate,
  onCommentUpdate,
  onCommentReply,
  onCommentDelete,
  onCommentLoadMore,
  onCommentReplyLoadMore,
} = useOrderComments(orderId);
const confirm = useConfirm();

const rules = {
  direction: {
    required: required(t('shipments.directions')),
  },
  billingTerms: {
    required: required(t('shipments.billingTerms')),
  },
  referenceNumbers: {},
  glCodes: {},
  internalNotes: {},
  carrierNotes: {},
  lineItems: {},
  equipmentType: {},
  requestedAccessorials: {},
  origin: {},
  destination: {},
  billTo: {},
  swc: {},
  customerRouted: {},
  hot: {},
  freightAllowance: {},
  valueOfGoods: {},
  requestedMode: {},
};
const { resetModel, setServerErrors, validate, v$ } = useValidations(
  order,
  rules,
  uid,
);
useUnsavedChangesConfirmation({
  isDirtyCallback: async () => isAnyDirty.value,
});

const saving = ref(false);
const loading = ref(false);
const canViewUplift = ref(false);
const numDirtyAccessorials = ref(0);
const numDirtyLineItems = ref(0);
const numDirtyRefNums = ref(0);
const numDirtyGlCodes = ref(0);
const numDirtyStops = ref(0);
const internalTags = ref([...order.value.tags]);
const tagCancelToken: Ref<CancelTokenSource | undefined> = ref();
const selectedCarrier: Ref<Loadable<Carrier>> = ref({ loading: false });
const shipOrders: Ref<Loadable<ShipOrder[]>> = ref({ loading: false });
const transportSegments: Ref<Loadable<ShipmentTransportSegment[]>> = ref({
  loading: false,
});
/** shipments created before the introduction of transport segments */
const legacyShipments: Ref<Loadable<Shipment[]>> = ref({ loading: false });
// TODO: Add some way to switch the selected shiporder/shipment to change
// the map. Should this update `selectedCarrierId` too and any other shipment
// specific data?
const selectedShipOrderIndex = ref(0);
const defaultOrderData: Ref<DefaultOrderData | undefined> = ref();
const assignableUsers: Ref<Loadable<UserRef[]>> = ref({ loading: false });
const mentionableUsers: Ref<Loadable<UserRef[]>> = ref({ loading: false });

const isCancellationModalVisible = ref(false);

const orderCloneModal = ref({
  visible: false,
  cloneReferenceNumbers: false,
  cloneTags: false,
  skipAutoActions: false,
  showRefernceNumberOption: false,
  showTagsOption: false,
});

const sharingModal = ref({
  isVisible: false,
  isLoading: false,
  accessToken: undefined as string | undefined,
});

const inviteCarriersToSpotMarketModal = ref({
  visible: false,
  sending: false,
  shipmentSpecId: undefined as number | undefined,
  inviteCommand: new ShipmentSpecInviteCarriersToSpotMarketCommand({
    orderId: orderId.value,
  }),
});

const spotMarketErrorModal = ref({
  visible: false,
  erroredSpotMarkets: [] as SpotMarket[],
});

const hasUpdatePermission = computed(() => {
  return authorization.hasAuthority('order', 'update');
});

const selectedShipment: Ref<Shipment | undefined> = computed(() => {
  const transportSegment =
    transportSegments.value.data?.[selectedShipOrderIndex.value];
  return transportSegment?.shipment;
});

// TODO: This needs to be updated after duplicating an order
const relatedShipments: Ref<Shipment[]> = computed(() => {
  if (transportSegments.value.loading) {
    return [];
  }

  const segments = transportSegments.value.data ?? [];
  const segmentShipments = segments.map((ts) => ts.shipment);

  return uniqBy(
    [...segmentShipments, ...(legacyShipments.value.data ?? [])],
    (shipment) => shipment.id,
  );
});

const showMap = computed(() =>
  order.value?.status
    ? !isStatusEqualOrBefore(order.value.status, OrderStatus.PENDING) &&
      !!selectedShipment.value
    : false,
);

const {
  isMapActionsEnabled,
  isMapExpanded,
  mapId,
  onMapDetailsButtonClick,
  routeGeoJson,
  showDynamicMap,
  staticMapUrl,
} = useMap(selectedShipment, { showMap });

const orderEditingLocked = computed(() => {
  return (
    isStatusEqualOrAfter(order.value.status, OrderStatus.COMPLETE) ||
    order.value.status === OrderStatus.CANCELED
  );
});

const orderShippingPoints = computed({
  get: () =>
    [order.value.origin, order.value.destination]
      .map((s) => s ?? new OrderShippingPoint(s))
      .filter(notNull),
  set: ([origin, destination]: OrderShippingPoint[]) => {
    order.value.origin = new OrderShippingPoint(origin);
    order.value.destination = new OrderShippingPoint(destination);
  },
});

const stopLevelAccessorials = computed({
  get(): { sequenceNumber: number; accessorials: AccessorialCode[] }[] {
    const result = [];
    if (order.value.origin) {
      result.push({
        sequenceNumber: 1,
        accessorials: order.value.origin.requestedAccessorials ?? [],
      });
    }
    if (order.value.destination) {
      result.push({
        sequenceNumber: 2,
        accessorials: order.value.destination.requestedAccessorials ?? [],
      });
    }
    return result;
  },
  set(newVal: { sequenceNumber: number; accessorials: AccessorialCode[] }[]) {
    newVal.forEach((accessorialsByStop) => {
      if (accessorialsByStop.sequenceNumber === 1 && order.value.origin) {
        order.value.origin.requestedAccessorials =
          accessorialsByStop.accessorials;
      } else if (
        accessorialsByStop.sequenceNumber === 2 &&
        order.value.destination
      ) {
        order.value.destination.requestedAccessorials =
          accessorialsByStop.accessorials;
      }
    });
  },
});

const searchParams = computed(() => {
  return router.currentRoute.value.query.search as string;
});

const isAnyDirty = computed(() => {
  return (
    v$.value.$anyDirty ||
    !!numDirtyLineItems.value ||
    !!numDirtyRefNums.value ||
    !!numDirtyGlCodes.value ||
    !!numDirtyAccessorials.value ||
    !!numDirtyStops.value
  );
});

const canDeleteTags = computed((): boolean =>
  authorization.hasAuthority('tag', 'manage'),
);

const isAssignedToShipment = computed(() => {
  return relatedShipments.value.length > 0;
});

const hasShipmentCarrierRate = computed(() => !!selectedCarrierId.value);

const selectedCarrierId = computed(() => {
  // TODO: What do we do when an order has multiple shiporders/shipments
  if (!transportSegments.value.data) {
    return;
  }

  const transportSegment = transportSegments.value.data?.[0];
  return transportSegment?.shipment.carrierId;
});

const selectedCarrierName = computed(() => {
  return selectedCarrier.value.data?.name ?? t('common.notYetSelected');
});

const availableEquipmentTypes = computed(() => {
  const clientTypes = clientEquipmentTypes.value.data ?? [];
  if (!selectedCarrierId.value) {
    return clientTypes;
  }

  const carrierTypes = selectedCarrier.value.data?.equipmentTypes ?? [];
  return clientTypes.concat(carrierTypes);
});

const shipmentTrackingUrl = computed(() => {
  const accessToken = sharingModal.value.accessToken;
  if (!accessToken || !order.value.id || !selectedShipment.value) {
    return [];
  }

  const suggestedEmails = [];
  const sharingEmailTypes = defaultOrderData.value?.sharingEmailTypes;
  if (
    sharingEmailTypes?.includes(SharingEmailType.ORIGIN) &&
    !!order.value.origin?.contact.contactEmail
  ) {
    suggestedEmails.push(order.value.origin.contact.contactEmail);
  }
  if (
    sharingEmailTypes?.includes(SharingEmailType.DESTINATION) &&
    !!order.value.destination?.contact.contactEmail
  ) {
    suggestedEmails.push(order.value.destination.contact.contactEmail);
  }

  return [
    {
      id: order.value.id,
      url: `${window.location.origin}/corsair/order/${orderId.value}/tracking?accessToken=${accessToken}`,
      suggestedEmails,
    },
  ];
});

/**
 * Loads the order and resets all current unsaved order state.
 * @param orderId The order to load
 * @param bypass A function to bypass setting the state with the new loaded
 * order. This is useful when user has unsaved changes and the new order doesn't
 * require a reload.
 */
const loadOrder = async (
  orderId: number,
  bypass?: (oldOrder: Order, newOrder: Order) => boolean,
): Promise<void> => {
  try {
    loading.value = true;
    const newOrder = await api.order.findById(orderId);

    if (bypass?.(order.value, newOrder)) {
      return;
    }

    order.value = newOrder;
    setParentEntity(order);

    // do everything else on the next tick so `parentEntity`s dependencies
    // have time to update
    nextTick(() => {
      loadRelatedTransportSegmentsAndShipments();
      loadRelatedLegacyShipments();
      loadRelatedShipOrders();
      getCanViewUpliftPermission(orderId);
      loadEquipmentTypes();
      loadTags();
      reset(order.value);
    });
  } catch (e) {
    if (axios.isCancel(e) || (await handleRequiredDomainRedirect(e))) {
      return;
    }

    console.error('Error loading order', orderId, e);
    toast.addError({
      error: e,
      detail: t('errors.loadingParamWithId', {
        param: t('orders.orders'),
        id: orderId,
      }),
    });
  } finally {
    loading.value = false;
  }
};

const saveOrder = async () => {
  if (!(await validate())) {
    return;
  }

  try {
    saving.value = true;
    order.value = await api.order.update(order.value);
    loadRelatedShipmentSpecsAndRates();
    loadRelatedTransportSegmentsAndShipments();
    loadRelatedLegacyShipments();
    loadRelatedShipOrders();
    reset(order.value);
    toast.addSuccess({
      detail: t('messages.successSavingParam', {
        param: t('orders.orders'),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error saving order', e);
    if (isBadRequest(e)) {
      const errorResponse = e.response?.data as ErrorResponse;
      setServerErrors(errorResponse);

      getFlattenedFieldErrors(errorResponse).forEach((error) => {
        toast.addError({
          detail: error,
          error: e,
        });
      });
    } else {
      toast.addError({
        error: e,
        detail: t('errors.savingParam', {
          param: t('orders.orders'),
        }),
      });
    }
  } finally {
    saving.value = false;
  }
};

const loadAssignableUsers = async () => {
  const ownerId = order.value.owner?.id;
  if (!ownerId) {
    console.error('Cannot determine owner id for order', order.value);
    return;
  }

  const usersLoadable = loadAssignableUsersByDomainId(ownerId, 'order');
  mentionableUsers.value = usersLoadable;
  assignableUsers.value = usersLoadable;
};

const loadRelatedShipOrders = async (): Promise<void> => {
  if (order.value.id == null) {
    throw new Error('Missing id for Order');
  }

  try {
    shipOrders.value.loading = true;
    shipOrders.value.data = await api.order.findAllRelatedShipOrders(
      order.value.id,
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error loading ship orders', order.value.id, e);
    shipOrders.value.data = undefined;
    shipOrders.value.error = e;
    toast.addError({
      error: e,
      detail: t('errors.loadingParamForParamWithId', {
        param: lowerCase(t('shipOrders.shipOrders')),
        param2: lowerCase(t('orders.orders')),
        id: order.value.id,
      }),
    });
  } finally {
    shipOrders.value.loading = false;
  }
};

const loadRelatedLegacyShipments = async (): Promise<void> => {
  if (order.value.id == null) {
    throw new Error('Missing id for Order');
  }

  try {
    legacyShipments.value.loading = true;
    legacyShipments.value.data = await api.order.findAllRelatedShipments(
      order.value.id,
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error loading related shipments', order.value.id, e);
    legacyShipments.value.data = undefined;
    legacyShipments.value.error = e;
    toast.addError({
      error: e,
      detail: t('errors.loadingParamForParamWithId', {
        param: lowerCase(t('shipments.shipments')),
        param2: lowerCase(t('orders.orders')),
        id: order.value.id,
      }),
    });
  } finally {
    legacyShipments.value.loading = false;
  }
};

const loadRelatedTransportSegmentsAndShipments = async (): Promise<void> => {
  if (order.value.id == null) {
    throw new Error('Missing id for Order');
  }

  try {
    transportSegments.value.loading = true;
    transportSegments.value.data =
      await api.order.findAllRelatedShipmentTransportSegments(order.value.id);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error loading transport segments', order.value.id, e);
    transportSegments.value.data = undefined;
    transportSegments.value.error = e;
    toast.addError({
      error: e,
      detail: t('errors.loadingParamForParamWithId', {
        param: lowerCase(t('shipOrders.shipOrders')),
        param2: lowerCase(t('orders.orders')),
        id: order.value.id,
      }),
    });
  } finally {
    transportSegments.value.loading = false;
  }
};

const loadCarrier = async (id: number) => {
  if (!authorization.hasAuthority('carrier', 'read')) {
    return;
  }

  try {
    selectedCarrier.value.loading = true;
    selectedCarrier.value.data = await api.carrier.findById(id);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    selectedCarrier.value.error = e;
    console.error('Error fetching carrier', e);
    toast.addError({
      detail: t('errors.loadingParam', { param: t('carriers.carriers') }),
      error: e,
    });
  } finally {
    selectedCarrier.value.loading = false;
  }
};

const reset = (order: Order) => {
  resetModel(order);
  numDirtyLineItems.value = 0;
  numDirtyRefNums.value = 0;
  numDirtyGlCodes.value = 0;
  numDirtyAccessorials.value = 0;
  numDirtyStops.value = 0;

  const l = lineItemSection.value as unknown as typeof LineItemSection;
  l?.reset();
  const s = locationSection.value as unknown as typeof LocationSection;
  s?.reset();
  const a = accessorialSection.value as unknown as typeof AccessorialSection;
  a?.reset();
};

const onOrderActionCommand = ({ action }: ActionCommand<OrderAction>) => {
  if (action === OrderAction.CANCEL) {
    isCancellationModalVisible.value = true;
  } else if (action === OrderAction.CLONE) {
    onClone();
  } else if (action === OrderAction.REQUEST_SPOT_MARKET_BID) {
    const shipmentSpec =
      shipmentSpecs.value.data?.length === 1
        ? shipmentSpecs.value.data[0]
        : undefined;
    confirmShowInviteCarriersToSpotMarket(shipmentSpec?.id);
  } else if (action === OrderAction.SHARE) {
    openSharingModal();
  }
};

const onClone = () => {
  orderCloneModal.value.showRefernceNumberOption =
    !!order.value.referenceNumbers?.length;

  orderCloneModal.value.showTagsOption = !!order.value.tags?.length;

  orderCloneModal.value.visible = true;
};

const onCancelOrder = async (event: CancelOrderPayload) => {
  try {
    saving.value = true;
    const canceledOrder = await api.order.cancel(orderId.value, {
      ...event,
      orderId: orderId.value,
    });
    reset(canceledOrder);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error cancelling order', e);
    toast.addError({
      detail: t('errors.cancelingParam', {
        param: t('orders.orders'),
      }),
      error: e,
    });
  } finally {
    saving.value = false;
  }
};

const onCloneOrder = async () => {
  try {
    saving.value = true;
    const command: CloneOrderCommand = {
      orderId: orderId.value,
      cloneReferenceNumbers: orderCloneModal.value.cloneReferenceNumbers,
      cloneTags: orderCloneModal.value.cloneTags,
      skipAutoActions: orderCloneModal.value.skipAutoActions,
    };
    const clone = await api.order.clone(orderId.value, command);
    router
      .push({
        name: 'orderDetail',
        params: { orderId: `${clone.id}` },
        query: { search: searchParams.value },
      })
      .then(() =>
        toast.addSuccess({
          detail: t('orders.messages.cloning'),
        }),
      );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error cloning order', e);
    toast.addError({
      detail: t('orders.errors.cloning'),
      error: e,
    });
  } finally {
    saving.value = false;
  }
};

const getCanViewUpliftPermission = async (orderId: number) => {
  try {
    canViewUplift.value = await api.user.getHasUpliftOrderPermission(orderId);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error(
      `Error fetching uplift permission for shipment ${orderId}`,
      e,
    );
    canViewUplift.value = false;
  }
};

const showInviteCarriersToSpotMarketModal = (shipmentSpecId?: number) => {
  inviteCarriersToSpotMarketModal.value.visible = true;
  inviteCarriersToSpotMarketModal.value.inviteCommand.orderId = orderId.value;
  inviteCarriersToSpotMarketModal.value.inviteCommand.shipmentSpecId =
    shipmentSpecId;
  inviteCarriersToSpotMarketModal.value.inviteCommand.equipmentType =
    order.value.equipmentType;
};

const confirmShowInviteCarriersToSpotMarket = (shipmentSpecId?: number) => {
  if (isAnyDirty.value) {
    confirm.require({
      group: 'rygen-confirm-dialog',
      message: t('carrierPortal.spotMarket.unsavedChangesWarning'),
      header: t('dirtyTracking.unsavedChanges'),
      icon: 'pi pi-exclamation-triangle text-warning',
      acceptClass: 'p-button-sm p-button-warn',
      rejectClass: 'p-button-sm p-button-text',
      acceptLabel: t('dirtyTracking.saveAndContinue'),
      rejectLabel: t('common.cancel'),
      accept: async () => {
        // When confirm dialog closes, PrimeVue will try to remove the
        // `p-overflow-hidden` class that blocks scrolling. It will keep trying
        // to remove this during saving so we add our own class too.
        // https://github.com/primefaces/primevue/issues/3953
        // https://github.com/primefaces/primevue/issues/3981
        document.body.classList.add('p-overflow-hidden');
        document.body.classList.add('p-overflow-hidden-force');

        await saveOrder();
        showInviteCarriersToSpotMarketModal(shipmentSpecId);

        // Wait for PrimeVue to stop trying to remove the class and add it back
        setTimeout(() => {
          document.body.classList.remove('p-overflow-hidden-force');
          document.body.classList.add('p-overflow-hidden');
        }, 300);
      },
    });
  } else {
    showInviteCarriersToSpotMarketModal(shipmentSpecId);
  }
};

const onCancelInvite = () => {
  inviteCarriersToSpotMarketModal.value.inviteCommand.shipmentSpecId =
    undefined;
};

const updateCarrierNotes = async (carrierNotes: string) => {
  try {
    const updatedOrder = await api.order.updateCarrierNotes(
      orderId.value,
      carrierNotes,
    );

    order.value.carrierNotes = updatedOrder.carrierNotes;
    resetModel(order.value);

    toast.addSuccess({
      detail: t('messages.successSavingParam', {
        param: t('orders.paramNotes', { param: t('carriers.carriers') }),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error saving carrier notes for order', e);
    toast.addError({
      error: e,
      detail: t('errors.savingParam', {
        param: t('orders.paramNotes', { param: t('carriers.carriers') }),
      }),
    });

    // Re-throw error so that we don't send spot market invite
    throw e;
  }
};

const onInviteCarriersToSpotMarket = async (
  command: ShipmentSpecInviteCarriersToSpotMarketCommand,
  carrierNotes: string | undefined,
) => {
  if (command.shipmentSpecId == null) {
    throw new Error(
      '`shipmentSpecId` is required to invite carriers to spot market',
    );
  }

  try {
    inviteCarriersToSpotMarketModal.value.sending = true;

    if (carrierNotes !== undefined) {
      await updateCarrierNotes(carrierNotes);
    }

    const result = await inviteCarriersToSpotMarket(command);
    inviteCarriersToSpotMarketModal.value.visible = false;

    if (result.validSpotMarkets.length) {
      orderRateShop.value?.showSpotMarketTab(command.shipmentSpecId);
    }

    startSpotMarketPollingForResult(result, {
      shipmentSpecId: command.shipmentSpecId,
    });

    if (result.erroredSpotMarkets.length) {
      spotMarketErrorModal.value.erroredSpotMarkets = result.erroredSpotMarkets;
      spotMarketErrorModal.value.visible = true;
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    // generic error handling happened within inviteCarriersToSpotMarket and
    // updateCarrierNotes. no specific handling needed
  } finally {
    inviteCarriersToSpotMarketModal.value.sending = false;
  }
};

const buildTagSearchRoute = (tag: string) => {
  return buildSearchRoute('orderList', {
    tags: [tag],
  });
};

const saveTags = async () => {
  if (tagCancelToken.value) {
    saveTagsDebounced.cancel();
    tagCancelToken.value.cancel();
  }
  tagCancelToken.value = axios.CancelToken.source();

  try {
    tags.value.loading = true;
    await api.order.updateTags(
      order.value,
      internalTags.value,
      tagCancelToken.value,
    );
    order.value.tags = internalTags.value;
    toast.addSuccess({
      detail: t('messages.successSavingParam', {
        param: t('tags.tags', 2),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error saving tags for order', e);
    tags.value.error = e;
    toast.addError({
      error: e,
      detail: t('errors.savingParam', { param: t('tags.tags', 2) }),
    });
  } finally {
    tags.value.loading = false;
  }
};
const saveTagsDebounced = debounce(saveTags, 1500);

const saveAssignedUsers = async (newVal: UserRef[]) => {
  if (!order.value.id) {
    return;
  }

  try {
    assignableUsers.value.loading = true;
    await api.order.updateAssignedUsers(order.value.id, newVal);
    order.value.assignedUsers = newVal;
    toast.addSuccess({
      detail: t('messages.successSavingParam', {
        param: t('users.users', 2),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error saving assigned users for order', e);
    toast.addError({
      error: e,
      detail: t('errors.savingParam', {
        param: t('common.assignParam', { param: t('users.users', 2) }),
      }),
    });
  } finally {
    assignableUsers.value.loading = false;
  }
};

const onAddTag = (tag: string) => {
  const newTag = new Tag({ name: tag });
  if (!tags.value.data?.includes(newTag)) {
    tags.value.data?.push(newTag);
  }
  saveTagsDebounced();
};

const onDelete = (tag: string) => {
  const tagToDelete = tags.value.data?.find((t: Tag) => t.name === tag);
  if (tagToDelete) {
    deleteTag(tagToDelete);
  }
};

const onRemoveTag = (_tag: string) => {
  saveTagsDebounced();
};

const openSharingModal = async () => {
  if (!selectedShipment.value?.id) {
    return;
  }

  try {
    sharingModal.value.isLoading = true;
    sharingModal.value.accessToken = await api.orderTrackingLink.getAccessToken(
      orderId.value,
    );
    sharingModal.value.isVisible = true;
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error loading access token for tracking link', e);
    toast.addError({
      error: e,
      detail: t('orders.errors.trackingLink'),
    });
  } finally {
    sharingModal.value.isLoading = false;
  }
};

const sendSharingEmails = async ({
  sharingRequest,
  confirmEmailSent,
}: {
  sharingRequest: SharingRequest;
  confirmEmailSent: () => void;
}) => {
  if (!selectedShipment.value?.id) {
    return;
  }

  try {
    await api.orderTrackingLink.sendEmails(orderId.value, sharingRequest);
    toast.addSuccess({
      detail: t('shipments.trackingLinkEmailSuccess'),
    });
    sharingModal.value.isVisible = false;
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    toast.addError({
      detail: t('shipments.errors.sharingEmail'),
      error: e,
    });
  } finally {
    confirmEmailSent();
  }
};

const loadDefaultOrderData = async () => {
  if (!authorization.hasAuthority('client', 'read')) {
    return;
  }

  try {
    defaultOrderData.value = await api.client.getDefaultOrderData();
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error getting default data', e);
    toast.addError({
      detail: t('errors.defaultData'),
      error: e,
    });
  }
};

const onRateShopCompleted = () => {
  loadRelatedTransportSegmentsAndShipments();
  loadRelatedLegacyShipments();
  loadRelatedShipOrders();

  // We want to only reload the order for certain actions taken (auto-tender)
  // otherwise user will lose unsaved changes. Ideally the rate shop would
  // return enough information to determine whether we should reload or not,
  // but for now only reload whenever status changes
  loadOrder(
    orderId.value,
    (oldOrder, newOrder) => oldOrder.status === newOrder.status,
  );
};

watch(
  isAssignedToShipment,
  (newVal) => {
    if (newVal) {
      loadRelatedShipmentSpecsAndRates();
    }
  },
  { deep: true, immediate: true },
);

watch(
  selectedCarrierId,
  (newVal: number | undefined) => {
    if (newVal) {
      loadCarrier(newVal);
    }
  },
  { deep: true, immediate: true },
);

watch(
  () => owningClient.value?.id,
  (newVal: number | undefined) => {
    if (newVal) {
      loadDefaultOrderData();
      loadClientBranding(newVal);
    }
  },
);

watch(
  orderId,
  async (newVal: number) => {
    clearParentEntity();
    shipOrders.value.data = undefined;
    selectedCarrier.value = { loading: false };

    await loadOrder(newVal);
    resetModel(order.value);
    loadAssignableUsers();
    loadComments();

    internalTags.value = [...order.value.tags];
  },
  { immediate: true },
);

watch(
  () => v$.value.lineItems,
  () => {
    if (!numDirtyLineItems.value) {
      v$.value.lineItems.$reset();
    }
  },
  { deep: true, immediate: true },
);

watch(
  () => v$.value.referenceNumbers,
  () => {
    if (!numDirtyRefNums.value) {
      v$.value.referenceNumbers.$reset();
    }
  },
  { deep: true, immediate: true },
);

watch(
  () => v$.value.glCodes,
  () => {
    if (!numDirtyGlCodes.value) {
      v$.value.glCodes.$reset();
    }
  },
  { deep: true, immediate: true },
);

watch(
  () => v$.value.requestedAccessorials,
  () => {
    if (!numDirtyAccessorials.value) {
      v$.value.requestedAccessorials.$reset();
    }
  },
  { deep: true, immediate: true },
);

watch(
  () => v$.value.origin,
  () => {
    if (!numDirtyStops.value) {
      v$.value.origin.$reset();
    }
  },
  { deep: true, immediate: true },
);

watch(
  () => v$.value.destination,
  () => {
    if (!numDirtyStops.value) {
      v$.value.destination.$reset();
    }
  },
  { deep: true, immediate: true },
);

onBeforeUnmount(() => {
  clearParentEntity();
});

provide('scope', uid);
provide('orderShippingPoints', orderShippingPoints);
</script>

<style>
.order-detail {
  .order-detail-card {
    .p-card-body {
      padding: 0;
    }
  }

  .order-detail-header {
    .order-detail-header-cell {
      position: relative;
      border-right: var(--divider);
      align-items: center;
      padding: var(--p-card-body-padding);
      flex: 1 0 auto;
      width: 14%;

      &:last-child {
        border-right: none;
      }

      .value {
        font-size: 18px;
      }

      .popup-inputgroup {
        position: static;

        & > .popup-inputgroup-edit-icon {
          top: var(--sp-3);
        }

        & > .popup-inputgroup-card {
          top: calc(var(--sp-2) * -1);
          left: var(--sp-2);
        }

        & > .popup-inputgroup-placeholder {
          .key-value-pair {
            .key-value-pair-label {
              text-transform: none;
            }
          }
        }
      }
    }
  }

  .information-section {
    .swc-icon,
    .customer-routed-icon {
      display: inline-block;
      vertical-align: middle;
    }
  }
}

body.p-overflow-hidden-force {
  overflow: hidden;
  padding-right: var(--p-scrollbar-width);
}
</style>
